import { SetURLSearchParams } from "react-router-dom";
import { Page } from "@flexidao/dto";
import { getFiltersFromUrl, setFiltersInUrl } from "./url-utils";
import { getFiltersFromLocalStorage, setFiltersInLocalStorage } from "./local-storage-utils";
import {
    FilterError,
    FilterErrorsWithKey,
    FiltersDecoder,
    FiltersValidators,
    SetFilterOptions,
} from "./types";

/**
 * Merges two objects, giving priority to the first one
 *
 * @param obj - The object to merge
 * @param fallback - The fallback object to merge with
 *
 * @returns The merged object
 */
export const mergeObjects = <O extends Record<string, unknown>, F extends Record<string, unknown>>({
    obj,
    fallback,
}: {
    obj: O;
    fallback: F;
}): O & F => ({
    ...fallback,
    ...obj,
});

/**
 * Validates the filters using the provided validators
 *
 * @param filters - The filters to validate
 * @param filtersValidators - The validators for the filters (optional)
 *
 * @returns An array of errors for each filter
 */
export const validateFilters = <FiltersType extends Record<string, unknown>>({
    filters,
    filtersValidators,
}: {
    filters: Partial<FiltersType>;
    filtersValidators?: FiltersValidators<FiltersType>;
}): Array<FilterErrorsWithKey<FiltersType>> =>
    filtersValidators
        ? Object.entries(filtersValidators).map(([key_, validator]) => {
              const key = key_ as keyof FiltersType;
              const value = filters[key];
              const valueErrors: Array<FilterError> = value && validator ? validator(value) : [];

              return {
                  key,
                  errors: valueErrors,
              };
          })
        : [];

/**
 * Get the filters from the URL and local storage
 *
 * @param searchParams - The URLSearchParams object
 * @param page - The page where the user is
 * @param tenantId - The tenant id of the user
 * @param defaultFilters - The default filters to use if no filters are found in the URL or local storage
 * @param filtersDecoder - The decoder for the filters
 * @param filtersValidators - The validators for the filters (optional)
 *
 * @returns The filters and the errors encountered while decoding and validating them
 */
export const getFilters = <FiltersType extends Record<string, unknown>>({
    searchParams,
    page,
    tenantId,
    defaultFilters,
    filtersDecoder,
    filtersValidators,
}: {
    searchParams: URLSearchParams;
    page: Page;
    tenantId: string;
    defaultFilters: FiltersType;
    filtersDecoder: FiltersDecoder<FiltersType>;
    filtersValidators?: FiltersValidators<FiltersType>;
}): {
    filters: FiltersType;
    urlFilterErrors: Array<FilterErrorsWithKey<FiltersType>>;
} => {
    // Get filters from URL and local storage
    const { data: filtersFromUrl, urlFilterErrors } = getFiltersFromUrl({
        searchParams,
        filtersDecoder,
        filtersValidators,
    });
    const filtersFromLocalStorage: Partial<FiltersType> | null = getFiltersFromLocalStorage({
        page,
        tenantId,
        filtersDecoder,
    });

    const completeFiltersFromLocalStorage: FiltersType = filtersFromLocalStorage
        ? mergeObjects({
              obj: filtersFromLocalStorage,
              fallback: defaultFilters,
          })
        : defaultFilters;

    // If the filters from URL are defined, we merge them with the filters from local storage (merged with the default ones)
    // Else, we use the filters from local storage (merged with the default ones)
    const finalFilters: FiltersType = filtersFromUrl
        ? mergeObjects({
              obj: filtersFromUrl,
              fallback: completeFiltersFromLocalStorage,
          })
        : completeFiltersFromLocalStorage;

    return {
        filters: finalFilters,
        urlFilterErrors,
    };
};

/**
 * Set the filters in local storage and in the URL
 *
 * @param filters - The filters to set
 * @param page - The page where the user is
 * @param tenantId - The tenant id of the user
 * @param setSearchParams - The function to set the search params
 * @param options - The options to set the filters in the URL (optional)
 *
 * @returns void
 */
export const setFilters = <FiltersType extends Record<string, unknown>>({
    filters,
    page,
    tenantId,
    setSearchParams,
    options,
}: {
    filters: FiltersType;
    page: Page;
    tenantId: string;
    setSearchParams: SetURLSearchParams;
    options?: SetFilterOptions;
}): void => {
    // Set filters in local storage
    setFiltersInLocalStorage({
        filters,
        page,
        tenantId,
    });

    // Set filters in URL
    setFiltersInUrl({
        filters,
        setSearchParams,
        options,
    });
};
